import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';

// Agregamos el icono 'Building' para la gestión de colegios
import { LayoutDashboard, Users, BookOpen, GraduationCap, LogOut, FileSpreadsheet, Home, Book, CalendarCheck, Briefcase, Settings, ShieldCheck, FileText, Shield, DollarSign, Megaphone, Building } from 'lucide-react';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [rolUsuario, setRolUsuario] = useState('');
  const [correoUsuario, setCorreoUsuario] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');

    try {
      const payloadBase64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const decodedJson = window.atob(payloadBase64);
      const payload = JSON.parse(decodedJson);

      let rolExtraido = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || payload.role || '';
      if (Array.isArray(rolExtraido)) rolExtraido = rolExtraido[0];
      
      const emailExtraido = payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || payload.email || 'Usuario';

      setRolUsuario(String(rolExtraido).trim());
      setCorreoUsuario(emailExtraido);
    } catch (error) {
      console.error(error);
      localStorage.removeItem('token');
      navigate('/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // --- REGLAS DE SEGURIDAD DEFINITIVAS ---
  const rolSeguro = String(rolUsuario).toUpperCase();

  // 1. Identificamos si es el dueño de la plataforma (Tú)
  const esSuperAdmin = rolSeguro === 'SUPERADMIN';

  // 2. Corregimos el rol: Ahora incluye ADMINCOLEGIO (El que creamos en el Onboarding)
  const tieneAccesoTotal = ['SUPERADMIN', 'ADMINCOLEGIO', 'DIRECTOR', 'ADMIN'].includes(rolSeguro);
  const esAdministrativo = tieneAccesoTotal || ['ADMINISTRATIVO'].includes(rolSeguro);
  const esAcademico = tieneAccesoTotal || ['DOCENTE', 'TUTOR', 'AUXILIAR'].includes(rolSeguro);
  const esComunidad = ['ESTUDIANTE', 'PADRE'].includes(rolSeguro);

  const linkClass = (path) => {
    const isActive = location.pathname === path || (path === '/dashboard' && location.pathname === '/');
    return `flex items-center gap-3 p-2.5 rounded-lg transition-colors text-sm font-medium ${isActive ? 'bg-blue-600 text-white shadow-md' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`;
  };

  return (
    <aside className="bg-slate-800 text-white w-64 hidden md:flex flex-col shadow-xl z-20 h-screen sticky top-0">
      <div className="p-4 bg-slate-900 border-b border-slate-700 flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <GraduationCap className="h-8 w-8 text-blue-400" />
          <span className="text-xl font-bold">EduControl</span>
        </div>
        
        <div className="mt-3 bg-slate-800 p-2 rounded border border-slate-700 flex items-center gap-2">
          <ShieldCheck size={16} className={esSuperAdmin ? "text-emerald-400" : tieneAccesoTotal ? "text-purple-400" : "text-green-400"}/>
          <div className="text-xs overflow-hidden">
            <p className="font-bold text-slate-300 truncate w-40" title={correoUsuario}>{correoUsuario}</p>
            <p className={esSuperAdmin ? "text-emerald-400 uppercase font-black tracking-wider" : "text-blue-400 uppercase font-black tracking-wider"}>
              {rolUsuario}
            </p>
          </div>
        </div>
      </div>
      
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600">
        <Link to="/dashboard" className={linkClass('/dashboard')}><LayoutDashboard size={18} /> Panel Principal</Link>

        {/* ========================================================= */}
        {/* BLOQUE EXCLUSIVO PARA EMD INGENIERÍA (SOLO SUPERADMIN)    */}
        {/* ========================================================= */}
        {esSuperAdmin && (
          <>
            <p className="text-[10px] font-bold text-emerald-500 mt-5 mb-2 uppercase px-2 tracking-wider">Centro de Mando EMD</p>
            
            <Link to="/sysadmin" className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors text-sm font-medium ${location.pathname === '/sysadmin' ? 'bg-emerald-600 text-white shadow-md' : 'text-emerald-100 hover:bg-slate-700 hover:text-white'}`}>
              <Building size={18} /> Suscripciones B2B
            </Link>
            
            <Link to="/auditoria" className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors text-sm font-medium ${location.pathname === '/auditoria' ? 'bg-emerald-600 text-white shadow-md' : 'text-emerald-100 hover:bg-slate-700 hover:text-white'}`}>
              <ShieldCheck size={18} /> Auditoría General
            </Link>
          </>
        )}

        {esAdministrativo && (
          <>
            <p className="text-[10px] font-bold text-slate-500 mt-5 mb-2 uppercase px-2 tracking-wider">Gestión Institucional</p>
            <Link to="/alumnos" className={linkClass('/alumnos')}><Users size={18} /> Alumnos</Link>
            <Link to="/apoderados" className={linkClass('/apoderados')}><Shield size={20} /> Padres / Apoderados</Link>
            <Link to="/caja" className={linkClass('/caja')}><DollarSign size={20} /> Caja y Pensiones </Link>
            <Link to="/comunicados" className={linkClass('/comunicados')}><Megaphone size={20} /> Comunicados</Link>
            <Link to="/matriculas" className={linkClass('/matriculas')}><FileSpreadsheet size={18} /> Matrículas</Link>
            <Link to="/aulas" className={linkClass('/aulas')}><Home size={18} /> Aulas y Grados</Link>
            <Link to="/cursos" className={linkClass('/cursos')}><Book size={18} /> Cursos</Link>
          </>
        )}

        {esAcademico && (
          <>
            <p className="text-[10px] font-bold text-slate-500 mt-5 mb-2 uppercase px-2 tracking-wider">Área Académica</p>
            <Link to="/calificaciones" className={linkClass('/calificaciones')}><BookOpen size={18} /> Calificaciones</Link>
            <Link to="/asistencias" className={linkClass('/asistencias')}><CalendarCheck size={18} /> Asistencias</Link>
            <Link to="/boletas" className={linkClass('/boletas')}><FileText size={18} /> Boletas de Notas</Link>
          </>
        )}

        {esComunidad && (
          <>
            <p className="text-[10px] font-bold text-slate-500 mt-5 mb-2 uppercase px-2 tracking-wider">Mi Portal</p>
            <button className="flex w-full items-center gap-3 text-slate-300 hover:bg-slate-700 hover:text-white p-2.5 rounded-lg text-sm font-medium"><BookOpen size={18} /> Mis Calificaciones</button>
            <button className="flex w-full items-center gap-3 text-slate-300 hover:bg-slate-700 hover:text-white p-2.5 rounded-lg text-sm font-medium"><CalendarCheck size={18} /> Mi Asistencia</button>
          </>
        )}

        {tieneAccesoTotal && (
          <>
            <p className="text-[10px] font-bold text-slate-500 mt-5 mb-2 uppercase px-2 tracking-wider">Configuración</p>
            <Link to="/personal" className={linkClass('/personal')}><Briefcase size={18} /> Personal y Accesos</Link>
            <Link to="/criterios" className={linkClass('/criterios')}><Settings size={18} /> Pesos de Evaluación</Link>
          </>
        )}
      </nav>

      <div className="p-4 border-t border-slate-700 bg-slate-900">
        <button onClick={handleLogout} className="flex items-center gap-3 text-slate-300 hover:text-red-400 hover:bg-slate-800 w-full p-2.5 rounded-lg font-medium transition-colors">
          <LogOut size={18} /> Cerrar Sesión
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;